<template>
    <v-container>
        <v-card-title class="text-uppercase justify-center">Dashboard</v-card-title>
        <v-row v-if="loading">
            <v-col v-for="card in cards" :key="card.key" cols="12" sm="6" lg="4">
                <v-skeleton-loader class="dashboard-skeleton" type="card" height="300"></v-skeleton-loader>
            </v-col>
        </v-row>

        <v-alert v-else-if="error" type="error" outlined>
            {{ $t('dashboardLoadError') }}
        </v-alert>

        <v-row v-else>
            <v-col v-for="card in cards" :key="card.key" cols="12" sm="6" lg="4">
                <v-card
                    class="dashboard-card"
                    :class="{'dashboard-card--link': card.to}"
                    :to="card.to || undefined"
                >
                    <v-card-title class="dashboard-card__title">
                        <v-icon class="mr-3" color="teal">{{ card.icon }}</v-icon>
                        {{ $t(card.title) }}
                    </v-card-title>

                    <v-card-text class="pt-0">
                        <router-link
                            v-for="metric in card.metrics.filter(metric => metric.to)"
                            :key="metric.key"
                            :to="metric.to"
                            class="dashboard-card__metric dashboard-card__metric-link"
                        >
                            <span>{{ $t(metric.label) }}</span>
                            <strong>{{ getMetricValue(card.key, metric.key) }}</strong>
                        </router-link>
                        <div
                            v-for="metric in card.metrics.filter(metric => !metric.to)"
                            :key="metric.key"
                            class="dashboard-card__metric"
                        >
                            <span>{{ $t(metric.label) }}</span>
                            <strong>{{ getMetricValue(card.key, metric.key) }}</strong>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import { DashboardService } from '../services';

    export default {
        name: "Home",
        data() {
            return {
                metaUrl: '/homepage',
                loading: true,
                error: false,
                dashboard: {},
                cards: [
                    {
                        key: 'clients',
                        title: 'dashboardClients',
                        icon: 'people',
                        metrics: [
                            {key: 'buyers', label: 'dashboardBuyers', to: '/users/buyers'},
                            {key: 'sellers', label: 'dashboardSellers', to: '/users/sellers'},
                            {key: 'new', label: 'dashboardNew', to: '/users/new'},
                        ],
                    },
                    {
                        key: 'products',
                        title: 'dashboardProducts',
                        icon: 'shopping_basket',
                        metrics: [
                            {key: 'total', label: 'dashboardTotal', to: '/products'},
                            {key: 'new', label: 'dashboardNew', to: '/products/new'},
                            {key: 'inStock', label: 'dashboardInStock'},
                            {key: 'notAvailable', label: 'dashboardNotAvailable'},
                            {key: 'lowLevel', label: 'dashboardLowLevel'},
                        ],
                    },
                    {
                        key: 'orders',
                        title: 'dashboardOrders',
                        icon: 'assignment',
                        to: '/orders',
                        metrics: [
                            {key: 'total', label: 'dashboardTotal'},
                            {key: 'review', label: 'dashboardReview'},
                            {key: 'inProgress', label: 'dashboardInProgress'},
                            {key: 'cancelled', label: 'dashboardCancelled'},
                            {key: 'completed', label: 'dashboardCompleted'},
                        ],
                    },
                    {
                        key: 'attributes',
                        title: 'dashboardAttributes',
                        icon: 'tune',
                        to: '/attributes',
                        metrics: [
                            {key: 'enum', label: 'dashboardEnumAttributes'},
                            {key: 'numeric', label: 'dashboardNumericAttributes'},
                        ],
                    },
                    {
                        key: 'categories',
                        title: 'dashboardCategories',
                        icon: 'category',
                        to: '/categories',
                        metrics: [
                            {key: 'total', label: 'dashboardTotal'},
                            {key: 'published', label: 'dashboardPublished'},
                            {key: 'hidden', label: 'dashboardHidden'},
                            {key: 'archived', label: 'dashboardArchived'},
                        ],
                    },
                    {
                        key: 'partnerManufacturerShowcases',
                        title: 'dashboardPartnerManufacturerShowcases',
                        icon: 'business',
                        to: '/partner-manufacturer-showcases',
                        metrics: [
                            {key: 'total', label: 'dashboardTotal'},
                            {key: 'partners', label: 'dashboardPartners'},
                            {key: 'manufacturers', label: 'dashboardManufacturers'},
                        ],
                    },
                ],
            }
        },
        methods: {
            getDashboard() {
                DashboardService.setAuth(this.$store.getters.token);
                DashboardService.getDashboard()
                    .then(res => {
                        this.dashboard = res.data;
                    })
                    .catch(err => {
                        console.log(err);
                        this.error = true;
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            getMetricValue(cardKey, metricKey) {
                const card = this.dashboard[cardKey] || {};

                return card[metricKey] || 0;
            },
        },
        created() {
            this.getDashboard();
        },
    }
</script>

<style scoped>
.dashboard-card,
.dashboard-skeleton {
    height: 300px;
}

.dashboard-card__title {
    font-size: 18px;
    font-weight: 500;
}

.dashboard-card__metric {
    align-items: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    display: flex;
    justify-content: space-between;
    min-height: 36px;
}

.dashboard-card--link {
    cursor: pointer;
}

.dashboard-card--link:focus,
.dashboard-card--link:hover {
    box-shadow: 0 5px 12px rgba(0, 0, 0, 0.16);
}

.dashboard-card__metric-link {
    color: inherit;
    text-decoration: none;
}

.dashboard-card__metric-link:focus,
.dashboard-card__metric-link:hover {
    color: #009688;
    text-decoration: none;
}

.dashboard-card__metric:last-child {
    border-bottom: 0;
}

.dashboard-card__metric strong {
    font-size: 16px;
}

</style>
